// Селектор режима: Ассистент / Переговорная / Конференция.
// Для «Переговорной» — кастомный список субагентов с иконками. Выбор сохраняется.

import { useEffect, useRef, useState } from 'react'
import {
  agentIconUrl,
  fetchAgents,
  getMeetingAgent,
  getMode,
  setMeetingAgent,
  setMode,
  type AgentInfo,
  type Mode,
} from '../net/mode'

const MODES: Array<{ key: Mode; label: string; hint: string }> = [
  { key: 'assistant', label: 'Ассистент', hint: 'Координатор напрямую' },
  { key: 'meeting', label: 'Переговорная', hint: 'выбранный агент' },
  { key: 'conference', label: 'Конференция', hint: 'Координатор + команда' },
]

export function ModeSelector({ onChange }: { onChange?: () => void }) {
  const [mode, setModeState] = useState<Mode>(getMode())
  const [agents, setAgents] = useState<AgentInfo[]>([])
  const [agent, setAgentState] = useState<string>(getMeetingAgent())
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchAgents().then((list) => {
      const specialists = list.filter((a) => !a.isDefault && a.id !== 'main')
      setAgents(specialists)
      if (!getMeetingAgent() && specialists[0]) {
        setMeetingAgent(specialists[0].id)
        setAgentState(specialists[0].id)
      }
    })
  }, [])

  // Закрытие списка по клику вне.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const chooseMode = (m: Mode) => {
    setMode(m)
    setModeState(m)
    onChange?.()
  }
  const chooseAgent = (id: string) => {
    setMeetingAgent(id)
    setAgentState(id)
    setOpen(false)
    onChange?.()
  }

  const current = MODES.find((m) => m.key === mode)
  const selected = agents.find((a) => a.id === agent)

  return (
    <div className="modes">
      <div className="modes__seg" role="tablist" aria-label="Режим работы">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={mode === m.key}
            className={`modes__btn ${mode === m.key ? 'modes__btn--active' : ''}`}
            onClick={() => chooseMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'meeting' ? (
        <div className="picker" ref={boxRef}>
          <button type="button" className="picker__btn" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
            <AgentFace id={selected?.id} emoji={selected?.emoji} />
            <span className="picker__name">{selected?.name || 'Агент…'}</span>
            <span className="picker__chev">{open ? '▴' : '▾'}</span>
          </button>
          {open && (
            <ul className="picker__list" role="listbox">
              {agents.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className={`picker__item ${a.id === agent ? 'picker__item--active' : ''}`}
                    role="option"
                    aria-selected={a.id === agent}
                    onClick={() => chooseAgent(a.id)}
                  >
                    <AgentFace id={a.id} emoji={a.emoji} />
                    <span>{a.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <span className="modes__hint">{current?.hint}</span>
      )}
    </div>
  )
}

function AgentFace({ id, emoji }: { id?: string; emoji?: string }) {
  const icon = agentIconUrl(id)
  if (icon) return <img className="picker__face" src={icon} alt="" />
  return <span className="picker__emoji">{emoji || '🤖'}</span>
}
